import { format, parseISO } from "date-fns";
import { UserIcon, PhoneIcon, MailIcon } from "@heroicons/react/outline";

import { FC } from "react";
import { NewCarBookingSuccessResponse, RentalPrice } from "../../api/types";
import { FormatMoney } from "../../utils/format";

interface Props {
  booking: NewCarBookingSuccessResponse;
  offer: RentalPrice;
}

const Confirmation: FC<Props> = ({ booking, offer }) => {
  const transmission = offer.isAutomatic ? "Automático" : "Manual";
  const imageSrc =
    offer.category.cars?.[0]?.pictures?.[0] ??
    process.env.REACT_APP_IMG_PATH + "/img/car-no-image.webp";

  return (
    <main className="tw-bg-white tw-px-4 tw-pt-10 tw-pb-8 sm:tw-px-6 sm:tw-pt-24 lg:tw-px-8 lg:tw-py-8 tw-shadow-md tw-rounded-md">
      <div className="tw-max-w-3xl tw-mx-auto">
        <div className="tw-max-w-xl">
          <h1 className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-indigo-600">
            Muchas gracias!
          </h1>
          <p className="tw-mt-2 tw-text-4xl tw-font-extrabold tw-tracking-tight sm:tw-text-5xl">
            Su renta está en proceso!
          </p>
          <p className="tw-mt-2 tw-text-base tw-text-gray-500">
            Su renta de auto <strong>{booking.number}</strong> ha sido recibida
            y se encuentra en proceso. Nos pondremos en contacto con usted lo
            antes posible.
          </p>

          <dl className="tw-mt-8 tw-text-sm tw-font-medium">
            <dt className="text-gray-900">Número de reserva</dt>
            <dd className="tw-text-indigo-600 tw-mt-2">{booking.number}</dd>
          </dl>
        </div>

        <section
          aria-labelledby="order-heading"
          className="tw-mt-10 tw-border-t tw-border-gray-200"
        >
          <h2 id="order-heading" className="tw-sr-only">
            Su renta de auto
          </h2>

          <h3 className="tw-sr-only">Items</h3>

          <div className="tw-py-6 tw-border-b tw-border-gray-200 tw-flex tw-space-x-6">
            <img
              alt={`${offer.category.nameSpanish} - ${transmission}`}
              src={imageSrc}
              className="tw-flex-none tw-w-20 tw-h-20 tw-object-center tw-object-cover tw-bg-gray-100 tw-rounded-lg sm:tw-w-40 sm:tw-h-40"
            />
            <div className="tw-flex-auto tw-flex tw-flex-col">
              <div>
                <h4 className="tw-font-medium tw-text-gray-900">
                  {offer.category.nameSpanish}
                </h4>
                <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
                  {transmission}
                </p>
                <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
                  <UserIcon className="tw-inline tw-w-4 tw-h-4 -tw-mt-1 tw-text-indigo-600 tw-mr-1" />{" "}
                  {booking.client.name}
                </p>
                <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
                  <MailIcon className="tw-inline tw-w-4 tw-h-4 -tw-mt-1 tw-text-indigo-600 tw-mr-1" />{" "}
                  {booking.client.email}
                </p>
                <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
                  <PhoneIcon className="tw-inline tw-w-4 tw-h-4 -tw-mt-1 tw-text-indigo-600 tw-mr-1" />{" "}
                  {booking.client.phone}
                </p>
              </div>
              <div className="tw-mt-6 tw-flex-1 tw-flex tw-items-end">
                <dl className="tw-flex tw-text-sm tw-divide-x tw-divide-gray-200 tw-space-x-4 sm:tw-space-x-6">
                  <div className="tw-flex">
                    <dt className="tw-font-medium tw-text-gray-900">
                      Días de renta
                    </dt>
                    <dd className="tw-ml-2 tw-text-gray-700">{offer.days}</dd>
                  </div>
                  <div className="tw-pl-4 tw-flex sm:tw-pl-6">
                    <dt className="tw-font-medium tw-text-gray-900">Precio</dt>
                    <dd className="tw-ml-2 tw-text-gray-700">
                      {FormatMoney(booking.pricing.total)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          <div className="sm:tw-ml-40 sm:tw-pl-6">
            <h3 className="tw-sr-only">Your information</h3>

            <h4 className="tw-sr-only">Addresses</h4>
            <dl className="tw-grid tw-grid-cols-2 tw-gap-x-6 tw-text-sm tw-py-6">
              <div>
                <dt className="tw-font-medium tw-text-gray-900">Recogida</dt>
                <dd className="tw-mt-2 tw-text-gray-700">
                  <span className="tw-block">
                    {`${format(
                      parseISO(booking.datePickup),
                      "dd MMMM yyyy"
                    )} - ${format(parseISO(booking.pickupTime), "hh:mm aa")}`}
                  </span>
                  <span className="tw-block">{booking.pickupPlace} </span>
                  <span className="tw-block"></span>
                </dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-900">Entrega</dt>
                <dd className="tw-mt-2 tw-text-gray-700">
                  <address className="tw-not-italic">
                    <span className="tw-block">
                      {`${format(
                        parseISO(booking.dateDropoff),
                        "dd MMMM yyyy"
                      )} - ${format(parseISO(booking.pickupTime), "hh:mm aa")}`}
                    </span>
                    <span className="tw-block">{booking.dropoffPlace} </span>
                  </address>
                </dd>
              </div>
            </dl>

            <h3 className="tw-sr-only">Summary</h3>

            <dl className="tw-space-y-4 tw-border-t tw-border-gray-200 tw-text-sm tw-pt-5">
              <div className="flex justify-between">
                <dt className="tw-text-base tw-font-medium tw-text-indigo-600">
                  A pagar en Cuba
                </dt>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-font-medium tw-text-gray-900">Depósito</dt>
                <dd className="tw-text-gray-700">
                  {FormatMoney(booking.pricing.deposit)}
                </dd>
              </div>

              <div className="tw-flex tw-justify-between">
                <dt className="tw-font-medium tw-text-gray-900">Seguro</dt>
                <dd className="tw-text-gray-700">
                  {booking.pricing.insurance === 0
                    ? "Incluído"
                    : FormatMoney(booking.pricing.insurance)}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between tw-border-t tw-border-gray-200 tw-pt-5">
                <dt className="tw-font-medium tw-text-gray-900">
                  Dias de renta
                </dt>
                <dd className="tw-text-gray-900">{offer.days}</dd>
              </div>
              <div className="tw-flex tw-justify-between">
                <dt className="tw-font-medium tw-text-gray-900">
                  Costo Diario
                </dt>
                <dd className="tw-text-gray-900">
                  {FormatMoney(booking.pricing.daily)}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between tw-border-t tw-border-b tw-border-gray-200 tw-pt-5 tw-pb-5">
                <dt className="tw-font-medium tw-text-gray-900">Subtotal</dt>
                <dd className="tw-text-gray-900">
                  {FormatMoney(offer.days * booking.pricing.daily)}
                </dd>
              </div>
              {offer.cost.gasFee > 0 && (
                <div className="tw-flex tw-justify-between">
                  <dt className="tw-font-medium tw-text-gray-900">
                    Cargo por Recogida
                  </dt>
                  <dd className="tw-text-gray-900">
                    {FormatMoney(offer.cost.pickupFee)}
                  </dd>
                </div>
              )}
              {offer.cost.gasFee > 0 && (
                <div className="tw-flex tw-justify-between">
                  <dt className="tw-font-medium tw-text-gray-900">
                    Cargo por combustible
                  </dt>
                  <dd className="tw-text-gray-900">
                    {FormatMoney(offer.cost.gasFee)}
                  </dd>
                </div>
              )}
              <div className="tw-flex tw-justify-between">
                <dt className="tw-font-medium tw-text-gray-900">
                  Tarifa pago por tarjeta de crédito
                </dt>
                <dd className="tw-text-gray-900">
                  {FormatMoney(booking.pricing.paymentGatewayFee)}
                </dd>
              </div>
              <div className="tw-flex tw-justify-between tw-border-t tw-border-gray-200 tw-pt-5">
                <dt className="tw-font-medium tw-text-gray-900">Total</dt>
                <dd className="tw-text-gray-900 tw-font-bold">
                  {FormatMoney(booking.pricing.total)}
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Confirmation;
